/**
 * Type definitions for Adobe Analytics tracking
 */
interface AdobeEventData {
  event?: string;
  eventData?: Record<string, string>;
}

interface AdobeDigData extends Array<AdobeEventData> {}

declare global {
  interface GlobalThis {
    digData?: AdobeDigData;
  }
}

/**
 * Event types for Adobe Analytics
 */
export const ADOBE_EVENT_TYPES = {
  LEARNING_PATH_STARTED: 'learningPathStarted',
  LEARNING_PATH_COMPLETED: 'learningPathCompleted',
  COURSE_STARTED: 'courseStarted',
  COURSE_COMPLETED: 'courseCompleted',
  CERTIFICATE_AWARDED: 'certificateAwarded',
  ACTION_CLICK: 'learningPathSelected', // As per analytics team we need to update the event name to learningPathSelected when a tile is clicked.
  PAGE_VIEW: 'pageView',
  EXERCISE_STARTED: 'exerciseStarted',
  EXERCISE_COMPLETED: 'exerciseCompleted',
  SDK_TOOLS_TILE_CLICK_EVENT: 'TileClick',
} as const;

/**
 * Action types for Adobe Analytics
 */
export const ADOBE_ACTION_TYPES = {
  LEARNING_PATH_STARTED: 'button click: learning path started',
  LEARNING_PATH_COMPLETED: 'button click: learning path completed',
  COURSE_STARTED: 'button click: course started',
  COURSE_COMPLETED: 'button click: course completed',
  CERTIFICATE_AWARDED: 'button click: certificate awarded',
  LEARNING_PATH_SELECTED: 'tile click: learning path selected',
  EXERCISE_STARTED: 'button click: coding exercise started',
  EXERCISE_COMPLETED: 'button click: coding exercise completed',
  SDK_TOOLS_TILE_CLICK: 'tile_click:',
} as const;

/**
 * Core function to track Adobe Analytics events
 * @param event - The event type to track
 * @param eventData - Additional data to attach to the event
 */
const trackAdobeAnalyticsEvent = (event: string, eventData: Record<string, string>): void => {
  try {
    // Validate globalThis and digData existence
    if (!globalThis?.digData || !Array.isArray(globalThis.digData) || globalThis.digData.length === 0) {
      /* eslint-disable no-console */
      console.warn('[Adobe Analytics] digData not available or empty');
      return;
    }

    // Find the last pageView event
    const pageViewIndex = globalThis.digData.map(obj => obj?.event).lastIndexOf(ADOBE_EVENT_TYPES.PAGE_VIEW);

    if (pageViewIndex < 0) {
      /* eslint-disable no-console */
      console.warn('[Adobe Analytics] No pageView event found in digData');
      return;
    }

    // Clone the pageView object and update it
    const pageViewObject = structuredClone(globalThis.digData[pageViewIndex]);
    pageViewObject.event = event;
    pageViewObject.eventData = {
      ...pageViewObject.eventData,
      ...eventData,
    };

    // Push the new event to digData
    globalThis.digData.push(pageViewObject);
  } catch (error) {
    /* eslint-disable no-console */
    console.error('[Adobe Analytics] Error tracking event:', error);
  }
};

/**
 * Track learning path events (started or completed)
 * @param eventType - Either 'learningPathStarted' or 'learningPathCompleted'
 * @param actionType - The action type describing the user interaction
 * @param learningPathName - The name of the learning path
 */
export const trackLearningPathAdobeAnalyticsEvent = (
  eventType: 'learningPathStarted' | 'learningPathCompleted',
  actionType: string,
  learningPathName: string
): void => {
  trackAdobeAnalyticsEvent(eventType, {
    actionType,
    learningPathName,
  });
};

/**
 * Track certificate awarded event
 * @param learningPathName - The name of the learning path for which the certificate was awarded
 */
export const trackCertificateAwardedAdobeAnalyticsEvent = (learningPathName: string): void => {
  trackAdobeAnalyticsEvent(ADOBE_EVENT_TYPES.CERTIFICATE_AWARDED, {
    actionType: ADOBE_ACTION_TYPES.CERTIFICATE_AWARDED,
    learningPathName,
  });
};

/**
 * Track code exercise events (attempted, completed, failed, etc.)
 * @param exerciseEvent - The specific exercise event type
 * @param learningPathName - The name of the learning path
 * @param courseName - The name of the course
 * @param exerciseName - The name of the exercise
 * @param actionType - The action type describing the user interaction
 */
export const trackCodeExerciseAdobeAnalyticsEvent = (
  exerciseEvent: string,
  learningPathName: string,
  courseName: string,
  exerciseName: string,
  actionType: string
): void => {
  trackAdobeAnalyticsEvent(exerciseEvent, {
    actionType,
    learningPathName,
    courseName,
    exerciseName,
  });
};

/**
 * Track course status events (started or completed)
 * @param learningPathName - The name of the learning path
 * @param courseName - The name of the course
 * @param event - The event type (e.g., 'courseStarted', 'courseCompleted')
 * @param actionType - The action type describing the user interaction
 */
export const trackCourseStatusAdobeAnalyticsEvent = (
  learningPathName: string,
  courseName: string,
  event: string,
  actionType: string
): void => {
  trackAdobeAnalyticsEvent(event, {
    actionType,
    learningPathName,
    courseName,
  });
};

/**
 * Track learning path title click event
 * @param learningPathName - The name of the learning path that was clicked
 * @param actionType - The action type (defaults to tile click)
 */
export const trackLearningPathTitleClickAdobeAnalyticsEvent = (
  learningPathName: string,
  actionType: string = ADOBE_ACTION_TYPES.LEARNING_PATH_SELECTED
): void => {
  trackAdobeAnalyticsEvent(ADOBE_EVENT_TYPES.ACTION_CLICK, {
    actionType,
    learningPathName,
  });
};

export const trackLearningPathSearchAdobeAnalyticsEvent = (
  searchTerm: string,
  searchFilters: string,
  resultCount: number
): void => {
  trackAdobeAnalyticsEvent('searchInteraction', {
    searchTerm,
    searchType: 'learning path search',
    searchFilters,
    resultCount: resultCount.toString(),
  });
};

export const trackSDKToolsTileClickAdobeAnalyticsEvent = (toolName: string, eventType: string): void => {
  trackAdobeAnalyticsEvent(ADOBE_EVENT_TYPES.SDK_TOOLS_TILE_CLICK_EVENT, {
    eventType,
    actionType: `${ADOBE_ACTION_TYPES.SDK_TOOLS_TILE_CLICK} ${toolName}`,
  });
};
